import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Pressable, StyleSheet } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { colors, spacing } from '../../theme';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

/**
 * The onboarding call-to-action: a forest-green leaf filling the bottom-right
 * corner, carrying either a bare arrow ("next") or an arrow with a label
 * ("Get Started") on the final slide.
 *
 * It is drawn as a path rather than composed from views because the corners do
 * not share a radius - the top-left is not a corner at all but a long wave
 * sweeping in from the left edge, while the other three are ordinary rounded
 * corners. `borderRadius`, even per-corner, cannot express that.
 *
 * It sits flush in the corner rather than floating with a margin, so it needs
 * no shadow: there is no gap under it to imply.
 */

/**
 * Corner height on a phone held upright, and the default.
 *
 * Exported because the screen that places this has to reserve exactly this
 * much room above it for its copy - two numbers that must agree, kept as one.
 */
export const BLOB_HEIGHT = 98;

/**
 * The same, on a screen under 700pt tall.
 *
 * A phone on its side has around 390pt of height to spend on a header, a
 * headline, two lines of body and this. At 98 the corner alone is a quarter of
 * that, and the artwork above it was squeezed to nothing. 72 is still a
 * generous target - comfortably past the 44pt minimum, and the shape is a
 * whole corner rather than a button, so it is easier to hit than its height
 * suggests.
 */
export const BLOB_HEIGHT_SHORT = 72;

const ARROW_WIDTH = 116;
/*
 * Sized to the label rather than padded out to a round number.
 *
 * The shape is anchored to the right edge, so its width and the position of
 * whatever sits inside it are the same control: narrowing it by 52 walks the
 * content 26 further right on screen. That is why this dropped from 240 while
 * the extra left padding came back out - together they leave "Get Started"
 * where it was and take the empty green off its left.
 */
const LABELLED_WIDTH = 188;

/**
 * Corner radii. All zero: every one of these corners meets a screen edge, so
 * rounding them left the shape floating in the corner rather than filling it.
 * The top-left is absent from the list entirely - it is not a corner but the
 * mouth of the wave, and it is the only curve the shape has.
 *
 * Kept as named values rather than deleted from the path: the geometry still
 * reads clearly, and a variant that does float only needs numbers here.
 */
const CORNERS = { topRight: 0, bottomRight: 0, bottomLeft: 0 };

/** How far the content travels when the slide changes. */
const SLIDE = 40;
const EXIT_MS = 150;
const ENTER_MS = 280;
/** How long the shape takes to grow or shrink between the two widths. */
const WIDTH_MS = 340;

/**
 * Side of the square the path is authored in.
 *
 * The shape is drawn once at 100x100 and stretched to whatever width the
 * container currently has, rather than regenerated for each width. Every handle
 * in `leafPath` is already a fraction of the box, so a path built at 100x100
 * and drawn with `preserveAspectRatio="none"` lands on exactly the same curve
 * as one built at the real size - and it means the width can be animated
 * without rebuilding the `d` string on every frame.
 */
const VIEWBOX = 100;

/**
 * The shape: one continuous curve from the bottom-left up to the top-right,
 * square edges everywhere else.
 *
 * Deliberately a single cubic. Two segments give a wave - the curvature
 * reverses where they meet, so the curve dips back inward before rising to the
 * corner and leaves a visible notch at the right-hand end. One segment can only
 * bulge one way, which is what makes it read as a single clean sweep.
 *
 * The handles are what keep it tangent to the edges it leaves and meets: the
 * first sits directly above the start point, so the curve leaves the left edge
 * travelling straight up; the second sits directly left of the end point, so it
 * arrives at the top edge travelling straight across. Move either off its axis
 * and the join with the straight edges becomes a corner.
 *
 * Handles are fractions of the box, so the curve keeps its proportions on both
 * the narrow arrow and the wide "Get Started" variant.
 */
const leafPath = (w: number, h: number) => {
  const { topRight: tr, bottomRight: br, bottomLeft: bl } = CORNERS;

  const endX = w - tr;

  /*
   * Each corner's quadratic is emitted only when it has a radius. At zero the
   * control point sits on the corner itself, so the curve is degenerate - some
   * renderers draw a stray nub there rather than nothing.
   */
  const parts = [
    `M0 ${h - bl}`,
    `C0 ${h * 0.34} ${w * 0.42} 0 ${endX} 0`,
  ];

  if (tr > 0) {
    parts.push(`Q${w} 0 ${w} ${tr}`);
  }
  parts.push(`L${w} ${h - br}`);

  if (br > 0) {
    parts.push(`Q${w} ${h} ${w - br} ${h}`);
  }
  parts.push(`L${bl} ${h}`);

  if (bl > 0) {
    parts.push(`Q0 ${h} 0 ${h - bl}`);
  }
  parts.push('Z');

  return parts.join(' ');
};

type Props = {
  /** Omit for the arrow-only shape used on every slide but the last. */
  label?: string;
  onPress: () => void;
  /**
   * How far above the bottom of the screen the shape stops. Android's
   * navigation bar can be translucent, so the green must end at the top of it
   * rather than bleed underneath and show through.
   */
  bottomGutter: number;
  accessibilityLabel: string;
  /**
   * The slide currently on screen. Every change replays the swap, so it fires
   * for a swipe just as it does for a tap.
   */
  pageIndex: number;
  /**
   * How tall the corner is. Defaults to `BLOB_HEIGHT`.
   *
   * The screen passes this rather than deciding it here, because the same
   * number also sets the clearance it keeps above the shape - the two have to
   * be read from one place or the copy ends up either overlapping the green or
   * floating well above it.
   */
  height?: number;
};

function BlobButton({
  label,
  onPress,
  bottomGutter,
  accessibilityLabel,
  pageIndex,
  height = BLOB_HEIGHT,
}: Props) {
  /*
   * What is currently painted, which lags `label` by one transition.
   *
   * React swaps the prop as soon as the page changes, so animating the live
   * value would play the exit on the content that is arriving - the new arrow
   * (or "Get Started") appears at rest, slides away, then slides back. Holding
   * the old value until the exit finishes means the thing that leaves is the
   * thing that was already there.
   */
  const [shown, setShown] = useState(label);

  const shift = useRef(new Animated.Value(0)).current;
  const fade = useRef(new Animated.Value(1)).current;
  /*
   * Width is animated rather than switched.
   *
   * It cannot use the native driver - width is a layout property, and the
   * native driver only handles transforms and opacity. That is fine here: one
   * view resizing for a third of a second is not the kind of work that drops
   * frames, and the alternative (scaleX on a fixed-width shape) would squash
   * the arrow and the label along with the green.
   */
  const boxWidth = useRef(
    new Animated.Value(label ? LABELLED_WIDTH : ARROW_WIDTH),
  ).current;

  // The first render is the resting state, not a transition.
  const settled = useRef(false);

  useEffect(() => {
    if (!settled.current) {
      settled.current = true;
      return;
    }

    const exit = Animated.parallel([
      Animated.timing(shift, {
        toValue: SLIDE,
        duration: EXIT_MS,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(fade, {
        toValue: 0,
        duration: EXIT_MS,
        useNativeDriver: true,
      }),
    ]);

    let enter: Animated.CompositeAnimation | undefined;

    exit.start(({ finished }) => {
      if (!finished) {
        return;
      }
      // Swap and jump left while nothing is visible, then bring it in.
      setShown(label);
      shift.setValue(-SLIDE);

      enter = Animated.parallel([
        Animated.timing(shift, {
          toValue: 0,
          duration: ENTER_MS,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(fade, {
          toValue: 1,
          duration: ENTER_MS,
          useNativeDriver: true,
        }),
        /*
         * The shape grows while the new content arrives, so the two read as
         * one gesture. Slightly longer than the content's own entrance, so
         * the green settles a beat after the label lands rather than with it.
         */
        Animated.timing(boxWidth, {
          toValue: label ? LABELLED_WIDTH : ARROW_WIDTH,
          duration: WIDTH_MS,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: false,
        }),
      ]);
      enter.start();
    });

    return () => {
      exit.stop();
      enter?.stop();
    };
    // `label` is read inside the callback; the transition is keyed off the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      onPress={onPress}
      style={({ pressed }) => [
        styles.container,
        { bottom: bottomGutter },
        pressed ? styles.pressed : null,
      ]}
    >
      {/*
        The Pressable is left unsized so it shrinks to this box, which means the
        touch target follows the animated width instead of staying at whichever
        value it was first laid out with.
      */}
      <Animated.View style={{ width: boxWidth, height }}>
        {/*
          Percentage size with a fixed viewBox: the Svg fills whatever width the
          animated box currently has and scales the 100x100 path into it.
          `preserveAspectRatio="none"` is what allows the two axes to scale
          independently - without it the shape would letterbox instead of
          stretching, and the curve would not reach the corners.
        */}
        <Svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
          preserveAspectRatio="none"
        >
          <Path d={leafPath(VIEWBOX, VIEWBOX)} fill={colors.primary} />
        </Svg>

        <Animated.View
          style={[
            styles.content,
            shown ? styles.contentLabelled : null,
            { opacity: fade, transform: [{ translateX: shift }] },
          ]}
          pointerEvents="none"
        >
          {shown ? (
            <Text variant="label" color={colors.onPrimary}>
              {shown}
            </Text>
          ) : null}
          <Icon
            name="arrowRight"
            size={36}
            color={colors.onPrimary}
            strokeWidth={1.6}
          />
        </Animated.View>
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    // Flush to the screen edge - the shape is a corner, not a floating button.
    right: 0,
  },
  pressed: {
    opacity: 0.85,
  },
  /*
   * Centred, then nudged down-right: the wave eats into the top-left of the
   * box, so the centre of the green mass sits inside the centre of the box.
   */
  content: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingLeft: spacing.md,
    paddingTop: spacing.lg,
  },
  /*
   * A small extra nudge only. The large one this used to carry existed to push
   * the label away from a left edge that was too far out; the box is now sized
   * to the label, so the padding no longer has to compensate for it.
   */
  contentLabelled: {
    paddingLeft: spacing.lg,
  },
});

export default BlobButton;
